/**
 * Cómo reacciona la mascota de los juegos a la última respuesta: contenta con un
 * acierto, "uy" con un error. Lo publican `answerCorrect()`, `answerWrong()` y
 * `celebrate()` (lib/confetti.ts), así ningún juego tiene que acordarse: la
 * mascota de `<GameShell>` reacciona sola, igual en todos.
 *
 * Es un solo dato para toda la app, como `lib/speaking.ts`: hay un juego por vez.
 * La reacción se apaga sola; una nueva pisa a la anterior.
 *
 * **Racha:** cada tres aciertos seguidos sin un error en el medio, el festejo es
 * una vuelta en el aire en vez del salto de siempre (`streak`). Un error la corta;
 * cambiar de vista también (`resetMascotStreak()`, lo llama useNav).
 */

import { useSyncExternalStore } from 'react';

export type MascotReaction = { mood: 'happy' | 'oops'; streak: boolean };

/** Cuánto dura cada una. `happy` cubre los dos saltos de la mascota
 *  (`mascot-hop`, 2 × --dur-celebrate); el festejo grande la deja un rato más. */
const HAPPY_MS = 1400;
const HAPPY_BIG_MS = 2600;
const OOPS_MS = 900;
const STREAK_EVERY = 3;

let reaction: MascotReaction | null = null;
let timer = 0;
let streak = 0;
const listeners = new Set<() => void>();

function set(next: MascotReaction | null): void {
  reaction = next;
  listeners.forEach((l) => l());
}

export function reactMascot(mood: MascotReaction['mood'], opts: { big?: boolean } = {}): void {
  if (typeof window === 'undefined') return;
  window.clearTimeout(timer);
  streak = mood === 'oops' ? 0 : streak + 1;
  set({ mood, streak: mood === 'happy' && streak % STREAK_EVERY === 0 });
  const ms = mood === 'oops' ? OOPS_MS : opts.big ? HAPPY_BIG_MS : HAPPY_MS;
  timer = window.setTimeout(() => set(null), ms);
}

export function resetMascotStreak(): void {
  streak = 0;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function useMascotReaction(): MascotReaction | null {
  return useSyncExternalStore(subscribe, () => reaction, () => null);
}
